import React, { useState, useRef, useEffect, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import defaultAvatar from './assets/images/default_avatar.png'

type UserRole = 'doctor' | 'patient'

/**
 * Server-persisted chat message shape (same as the doctor consultation chat).
 */
interface ChatMessage {
  id: string | number
  room_id?: number
  sender_id: number
  message: string
  message_type?: 'text' | 'image' | 'file'
  metadata?: {
    s3_key?: string
    file_name?: string
    file_size?: number
    content_type?: string
    presigned_url?: string
  } | null
  created_at?: string
  /** Set on our own message when the server did not acknowledge it. */
  failed?: boolean
}

interface ChatPanelProps {
  /** Consultation access JWT (from the join link). Used for socket auth + REST. */
  authToken?: string
  /**
   * Returns a currently-valid access token, refreshing it when it is expired or about to
   * expire. Access tokens live 15 minutes and the gateway re-checks the token the socket was
   * opened with on EVERY chat event, so the chat must reconnect with a fresh one in time.
   */
  getAuthToken?: () => Promise<string | null>
  /** Appointment id — scopes the chat room to this consultation. */
  appointmentId?: number
  /** Current participant role. Drives counterpart resolution. */
  role?: UserRole
  /** Counterpart platform user id, if already known (skips REST resolution). */
  otherUserId?: number
  /** API base url including /api/v1 (e.g. http://localhost:8000/api/v1). */
  apiBaseUrl?: string
  /** Display name of the current participant, shown on their own messages. */
  selfName?: string
  /** Display name of the counterpart, shown on their messages. */
  otherName?: string
}

const ROLE_LABELS: Record<UserRole, string> = {
  doctor: 'Doctor',
  patient: 'Patient'
}

/** Decode a base64url segment (JWT payload) in browser/node. */
const decodeBase64Url = (encoded: string): string => {
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
  const padding = normalized.length % 4 === 0 ? 0 : 4 - (normalized.length % 4)
  const padded = normalized.concat('='.repeat(padding))
  if (typeof globalThis.atob === 'function') return globalThis.atob(padded)
  if (typeof Buffer !== 'undefined')
    return Buffer.from(padded, 'base64').toString('utf-8')
  return ''
}

/** Expiry of a JWT in epoch milliseconds, or null when it cannot be read. */
const tokenExpiryMs = (token?: string | null): number | null => {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(decodeBase64Url(parts[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * The viewer's IANA time zone as detected by the browser (e.g. "Asia/Kolkata"). Undefined when
 * the browser cannot report one, in which case Intl falls back to the device's local zone anyway.
 */
const detectTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

/** Build a formatter in the viewer's zone, retrying without it if the zone id is rejected. */
const makeFormatter = (
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  // 'en-US' keeps the "2:52 PM" shape (and Latin digits) the rest of the UI uses, whatever the
  // browser language is; the time zone is still the viewer's own.
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: detectTimeZone()
    })
  } catch {
    return new Intl.DateTimeFormat('en-US', options)
  }
}

/** "2:52 PM" — 12-hour clock in the viewer's time zone. */
const TIME_FORMATTER = makeFormatter({
  hour: 'numeric',
  minute: '2-digit',
  hour12: true
})

/** "Fri, Oct 9, 2026, 2:52 PM GMT+5:30" — tooltip with the date and the zone it is shown in. */
const FULL_TIMESTAMP_FORMATTER = makeFormatter({
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZoneName: 'short'
})

/**
 * Parse a message timestamp. The chat service sends UTC ISO strings ("...T10:05:00.000Z"); a
 * value without an explicit offset is also treated as UTC, because JS would otherwise read it as
 * the viewer's local time and shift every message by their UTC offset.
 */
const parseMessageDate = (value?: string): Date | null => {
  if (!value) return new Date()
  const trimmed = value.trim()
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)
  const isDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed)
  const normalized =
    isDateTime && !hasOffset ? `${trimmed.replace(' ', 'T')}Z` : trimmed
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

/** How long before expiry the chat reconnects with a fresh token. */
const TOKEN_RENEW_MARGIN_MS = 60 * 1000
/** Consecutive failed (re)connections after which the chat gives up and says so. */
const MAX_CONNECT_FAILURES = 5

/** Extract the current user's platform id from the JWT. */
const decodeUserId = (token?: string): number | null => {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(decodeBase64Url(parts[1]))
    const id = payload.userId ?? payload.user_id ?? payload.sub ?? payload.id
    return id != null ? Number(id) : null
  } catch {
    return null
  }
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  authToken,
  getAuthToken,
  appointmentId,
  role = 'doctor',
  otherUserId,
  apiBaseUrl,
  selfName,
  otherName
}) => {
  const [activeTab] = useState<'chat' | 'prescription'>('chat')
  const [inputValue, setInputValue] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  // Sender avatars (presigned URLs): `self` for the current user, `other` for the counterpart.
  const [avatars, setAvatars] = useState<{
    self: string | null
    other: string | null
  }>({ self: null, other: null })

  // 'connecting' until the room is joined, 'ready' while usable, 'unavailable' after giving up.
  const [chatStatus, setChatStatus] = useState<
    'connecting' | 'ready' | 'unavailable'
  >('connecting')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket | null>(null)
  const roomIdRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Latest props, read by the long-lived socket without re-creating it on every token refresh.
  const authTokenRef = useRef<string | undefined>(authToken)
  authTokenRef.current = authToken
  const getAuthTokenRef = useRef<ChatPanelProps['getAuthToken']>(getAuthToken)
  getAuthTokenRef.current = getAuthToken
  // Token the CURRENT socket connection was opened with (the gateway re-verifies this one).
  const connectedTokenRef = useRef<string | null>(null)

  /** A valid token for the next (re)connection — fresh from the App when it can provide one. */
  const resolveToken = useCallback(async (): Promise<string | null> => {
    try {
      const fresh = getAuthTokenRef.current
        ? await getAuthTokenRef.current()
        : null
      return fresh || authTokenRef.current || null
    } catch (err) {
      console.warn('[AGORA_CHAT] Could not refresh the access token:', err)
      return authTokenRef.current || null
    }
  }, [])

  const rawBase =
    apiBaseUrl ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://localhost:8000/api/v1'
  // The socket gateway listens at the host root (default /socket.io/), not under /api/v1.
  const socketUrl = (() => {
    try {
      return new URL(rawBase).origin
    } catch {
      return 'http://localhost:8000'
    }
  })()
  const restBase = rawBase.replace(/\/$/, '')

  const selfUserId = decodeUserId(authToken)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  /**
   * Resolve the counterpart's platform user id AND both participants' avatars from the
   * appointment detail (same source the doctor app uses). Prefers an explicit
   * `otherUserId` prop for the id, but still fetches the appointment for avatars.
   */
  const resolveParticipants = useCallback(async (): Promise<number | null> => {
    const propId =
      otherUserId && Number(otherUserId) > 0 ? Number(otherUserId) : null

    if (!appointmentId || !authTokenRef.current) return propId

    const endpoint =
      role === 'doctor'
        ? `${restBase}/consultation/doctor/appointments/${appointmentId}`
        : `${restBase}/patient/appointments/${appointmentId}`

    try {
      const token = await resolveToken()
      const res = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!res.ok) return propId
      const json = await res.json()
      const appt = json?.data || json || {}

      // Counterpart AUTH user id (probe the common shapes across the platform).
      // `appt.doctor_id` / `doctor.doctor_id` are deliberately NOT candidates: they are the
      // doctors-table primary key, not a user id, and using one created a chat room with the
      // wrong recipient — the patient's messages then never reached the doctor.
      // (`appt.patient_id` IS the patient's auth user id, so it stays.)
      const idCandidates =
        role === 'doctor'
          ? [
              appt?.patient?.user_id,
              appt?.patient_user_id,
              appt?.raw?.patient?.user_id,
              appt?.patient_id
            ]
          : [
              appt?.doctor?.user_id,
              appt?.doctor_user_id,
              appt?.raw?.doctor?.user_id
            ]
      const foundId = idCandidates.find((v) => v != null && Number(v) > 0)

      // Patient + doctor avatar presigned URLs (both endpoint shapes covered).
      const firstString = (...vals: any[]): string | null => {
        const v = vals.find((x) => typeof x === 'string' && x.length > 0)
        return (v as string) || null
      }
      const patientAvatar = firstString(
        appt?.patient_avatar_presigned_url,
        appt?.patient?.avatar_presigned_url,
        appt?.raw?.patient?.avatar_presigned_url,
        appt?.patient?.avatar
      )
      const doctorAvatar = firstString(
        appt?.doctor_avatar_presigned_url,
        appt?.doctor?.avatar_presigned_url,
        appt?.raw?.doctor?.avatar_presigned_url,
        appt?.doctor?.avatar
      )

      setAvatars((prev) => ({
        self: (role === 'doctor' ? doctorAvatar : patientAvatar) || prev.self,
        other: (role === 'doctor' ? patientAvatar : doctorAvatar) || prev.other
      }))

      return propId ?? (foundId != null ? Number(foundId) : null)
    } catch (err) {
      console.warn('[AGORA_CHAT] Failed to resolve participants:', err)
      return propId
    }
  }, [otherUserId, appointmentId, role, restBase, resolveToken])

  /** Avatar URL for a message sender (own messages vs counterpart). */
  const avatarForSender = (senderId: number): string | null =>
    senderId === selfUserId ? avatars.self : avatars.other

  // Sender names; fall back to the role when the name is not known.
  const counterpartRole: UserRole = role === 'doctor' ? 'patient' : 'doctor'
  const selfDisplayName = selfName?.trim() || ROLE_LABELS[role]
  const otherDisplayName = otherName?.trim() || ROLE_LABELS[counterpartRole]
  const nameForSender = (senderId: number): string =>
    senderId === selfUserId ? selfDisplayName : otherDisplayName

  /**
   * Fetch the current user's own avatar from /consultation/me (role-agnostic).
   * The doctor appointment detail doesn't include the doctor's own avatar, so this
   * ensures the sender's own messages also show their image.
   */
  const fetchSelfAvatar = useCallback(async (): Promise<void> => {
    if (!authTokenRef.current) return
    try {
      const token = await resolveToken()
      const res = await fetch(`${restBase}/consultation/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!res.ok) return
      const json = await res.json()
      const me = json?.data || json || {}
      const selfAvatar =
        me?.avatar_presigned_url ||
        me?.user?.avatar_presigned_url ||
        me?.avatar ||
        me?.user?.avatar ||
        null
      if (selfAvatar) {
        setAvatars((prev) => ({ ...prev, self: selfAvatar }))
      }
    } catch (err) {
      console.warn('[AGORA_CHAT] Failed to fetch self avatar:', err)
    }
  }, [restBase, resolveToken])

  // Real-time chat connection (same flow as the doctor consultation chat).
  //
  // WHY THE TOKEN HANDLING LOOKS LIKE THIS: access tokens live 15 minutes, and the gateway
  // re-verifies the token the socket was OPENED with on every chat event. The old code opened
  // the socket once with the token from the join link, so chat stopped working as soon as that
  // token expired — and a socket rejected at connect time is never retried by socket.io. Now:
  //   - every (re)connection asks `resolveToken()` for a currently-valid token (`auth` callback);
  //   - the socket reconnects shortly before that token expires;
  //   - a rejected connection is retried with a fresh token, a bounded number of times.
  //
  // The socket is created once per appointment; a token refresh no longer tears it down.
  useEffect(() => {
    if (!authTokenRef.current) {
      setLoading(false)
      setChatStatus('unavailable')
      return
    }

    let cancelled = false
    let renewTimer: number | undefined
    let retryTimer: number | undefined
    let connectFailures = 0
    // The counterpart only has to be resolved once, not on every reconnect.
    let participantsPromise: Promise<number | null> | null = null

    const socket = io(socketUrl, {
      // Called by socket.io before EVERY (re)connection, so each handshake carries a fresh token.
      auth: (sendAuth: (data: object) => void) => {
        resolveToken()
          .then((token) => {
            connectedTokenRef.current = token
            sendAuth({ token: token || '' })
          })
          .catch(() => {
            connectedTokenRef.current = authTokenRef.current || null
            sendAuth({ token: authTokenRef.current || '' })
          })
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      forceNew: false
    })
    socketRef.current = socket

    /** Reconnect so the next handshake carries a fresh token. */
    const reconnectWithFreshToken = (reason: string) => {
      if (cancelled) return
      console.log(`[AGORA_CHAT] Reconnecting chat (${reason})`)
      window.clearTimeout(renewTimer)
      socket.disconnect()
      socket.connect()
    }

    /** Reconnect just before the token this connection was opened with expires. */
    const scheduleTokenRenewal = () => {
      window.clearTimeout(renewTimer)
      const expiresAt = tokenExpiryMs(connectedTokenRef.current)
      if (!expiresAt) return
      const delay = Math.max(
        expiresAt - Date.now() - TOKEN_RENEW_MARGIN_MS,
        15 * 1000
      )
      renewTimer = window.setTimeout(
        () => reconnectWithFreshToken('access token about to expire'),
        delay
      )
    }

    socket.on('connect', async () => {
      // eslint-disable-next-line no-console
      console.log('[AGORA_CHAT] socket connected:', socket.id)
      connectFailures = 0
      scheduleTokenRenewal()

      // Load avatars in the background — this must NOT block room creation, otherwise
      // a slow/failing appointment fetch would leave the chat unable to send messages.
      if (!participantsPromise) {
        fetchSelfAvatar()
        participantsPromise = resolveParticipants()
        participantsPromise.catch(() => undefined)
      }

      // Prefer the counterpart id passed via props so the room is created immediately;
      // only wait on the appointment fetch when we don't already have the id.
      const propId =
        otherUserId && Number(otherUserId) > 0 ? Number(otherUserId) : null
      const counterpart = propId ?? (await participantsPromise)
      if (cancelled) return
      if (!counterpart) {
        console.warn('[AGORA_CHAT] No counterpart user id; chat disabled.')
        setLoading(false)
        setChatStatus('unavailable')
        return
      }

      const roomArgs: Record<string, number> = { other_user_id: counterpart }
      if (appointmentId) roomArgs.appointment_id = Number(appointmentId)

      socket.emit('get_or_create_room', roomArgs, (ack: any) => {
        if (cancelled) return
        if (ack?.success && ack?.room_id) {
          const roomId = ack.room_id
          roomIdRef.current = roomId
          socket.emit('join_room', { room_id: roomId })
          socket.emit(
            'get_messages',
            { room_id: roomId, page: 1, limit: 100 },
            (msgAck: any) => {
              if (cancelled) return
              if (msgAck?.success && Array.isArray(msgAck.messages)) {
                // Keep any of our own messages that failed to send, so they stay visible.
                setMessages((prev) => [
                  ...msgAck.messages,
                  ...prev.filter((m) => m.failed)
                ])
              }
              setLoading(false)
              setChatStatus('ready')
            }
          )
        } else {
          console.warn('[AGORA_CHAT] get_or_create_room failed:', ack)
          setLoading(false)
          setChatStatus('unavailable')
        }
      })
    })

    socket.on('disconnect', (reason: string) => {
      if (cancelled) return
      console.log('[AGORA_CHAT] socket disconnected:', reason)
      // A client-initiated disconnect is our own token renewal; it reconnects immediately.
      if (reason !== 'io client disconnect') setChatStatus('connecting')
    })

    // An event rejected by the gateway (e.g. its auth guard) arrives as `exception` and the
    // event's acknowledgement never fires. Reconnect when our token is the likely cause.
    socket.on('exception', (payload: any) => {
      console.warn('[AGORA_CHAT] gateway rejected a chat event:', payload)
      const expiresAt = tokenExpiryMs(connectedTokenRef.current)
      if (expiresAt && expiresAt - Date.now() < TOKEN_RENEW_MARGIN_MS) {
        reconnectWithFreshToken('access token expired')
      }
    })

    socket.on('new_message', (data: any) => {
      // Only handle messages for this room, and ignore our own (already shown optimistically).
      if (roomIdRef.current && data.room_id !== roomIdRef.current) return
      if (data.sender_id === selfUserId) return
      setMessages((prev) => {
        if (prev.some((m) => m.id === data.message_id)) return prev
        return [
          ...prev,
          {
            id: data.message_id,
            room_id: data.room_id,
            sender_id: data.sender_id,
            message: data.message,
            message_type: data.message_type || 'text',
            metadata: data.metadata || null,
            created_at: data.timestamp || new Date().toISOString()
          }
        ]
      })
    })

    socket.on('connect_error', (err: any) => {
      if (cancelled) return
      console.warn('[AGORA_CHAT] socket connect_error:', err?.message || err)
      // `socket.active` is false when the SERVER refused the connection (the gateway's auth
      // middleware answers "Authentication failed"). socket.io never retries those on its own,
      // so retry here — each attempt asks for a fresh token — and give up visibly after a few.
      if (socket.active) return
      connectFailures += 1
      if (connectFailures > MAX_CONNECT_FAILURES) {
        console.error(
          '[AGORA_CHAT] Chat connection refused repeatedly; giving up.'
        )
        setLoading(false)
        setChatStatus('unavailable')
        return
      }
      setChatStatus('connecting')
      window.clearTimeout(retryTimer)
      retryTimer = window.setTimeout(() => {
        if (!cancelled) socket.connect()
      }, 2000 * connectFailures)
    })

    return () => {
      cancelled = true
      window.clearTimeout(renewTimer)
      window.clearTimeout(retryTimer)
      if (roomIdRef.current)
        socket.emit('leave_room', { room_id: roomIdRef.current })
      socket.disconnect()
      socketRef.current = null
    }
    // The socket lives for the whole consultation: tokens are read through refs and
    // `resolveToken`, so a token refresh must NOT re-create it.
  }, [appointmentId, role, socketUrl])

  /**
   * Send through the socket and wait for the gateway's acknowledgement. Our own message is shown
   * straight away (the gateway does not echo it back) and flagged if the server never confirms
   * it, instead of silently pretending it was delivered.
   */
  const emitMessage = (
    tempId: string,
    payload: {
      room_id: number
      message: string
      message_type: 'text' | 'image' | 'file'
      metadata?: ChatMessage['metadata']
    }
  ) => {
    const socket = socketRef.current
    if (!socket) return
    socket
      .timeout(10000)
      .emit('send_message', payload, (err: unknown, ack: any) => {
        if (!err && ack?.success) return
        console.warn('[AGORA_CHAT] Message was not acknowledged:', err || ack)
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, failed: true } : m))
        )
      })
  }

  const handleSend = () => {
    const text = inputValue.trim()
    if (!text) return
    const socket = socketRef.current
    const roomId = roomIdRef.current
    if (!socket || !socket.connected || !roomId || selfUserId == null) {
      console.warn('[AGORA_CHAT] Cannot send — chat not ready.')
      return
    }

    const tempId = `temp_${Date.now()}`
    emitMessage(tempId, {
      room_id: roomId,
      message: text,
      message_type: 'text'
    })

    // Optimistically render our own message (the gateway does not echo to the sender).
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        room_id: roomId,
        sender_id: selfUserId,
        message: text,
        message_type: 'text',
        metadata: null,
        created_at: new Date().toISOString()
      }
    ])
    setInputValue('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  /**
   * Upload the selected file and send it as a chat message (image or file), then
   * optimistically render it. Mirrors the doctor consultation chat upload flow.
   */
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    // Reset the input so selecting the same file again still fires onChange.
    e.target.value = ''
    if (!file) return

    const socket = socketRef.current
    const roomId = roomIdRef.current
    if (!socket || !socket.connected || !roomId || selfUserId == null) {
      console.warn('[AGORA_CHAT] Cannot send file — chat not ready.')
      return
    }

    const isImage = file.type.startsWith('image/')
    const messageType: 'image' | 'file' = isImage ? 'image' : 'file'
    setUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)
      // Note: do NOT set Content-Type manually — the browser adds the multipart boundary.
      const uploadToken = await resolveToken()
      const res = await fetch(`${restBase}/consultation/chat/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${uploadToken}` },
        body: formData
      })
      const json = await res.json()
      const s3Key = json?.data?.s3_key
      const presignedUrl = json?.data?.presigned_url
      if (!s3Key) throw new Error('Upload failed')

      const metadata = {
        s3_key: s3Key,
        file_name: file.name,
        file_size: file.size,
        content_type: file.type,
        presigned_url: presignedUrl
      }

      const tempId = `temp_${Date.now()}`
      emitMessage(tempId, {
        room_id: roomId,
        message: file.name,
        message_type: messageType,
        metadata
      })

      // Optimistically render our own file message (the gateway does not echo to sender).
      setMessages((prev) => [
        ...prev,
        {
          id: tempId,
          room_id: roomId,
          sender_id: selfUserId,
          message: file.name,
          message_type: messageType,
          metadata,
          created_at: new Date().toISOString()
        }
      ])
    } catch (err) {
      console.error('[AGORA_CHAT] File upload failed:', err)
    } finally {
      setUploading(false)
    }
  }

  const formatTime = (value?: string) => {
    const date = parseMessageDate(value)
    return date ? TIME_FORMATTER.format(date) : ''
  }

  /** Full date, time and zone for the hover tooltip, e.g. "Fri, Oct 9, 2026, 2:52 PM GMT+5:30". */
  const formatFullTimestamp = (value?: string) => {
    const date = parseMessageDate(value)
    return date ? FULL_TIMESTAMP_FORMATTER.format(date) : ''
  }

  const renderMessageBody = (message: ChatMessage) => {
    const isFile =
      message.message_type === 'file' || message.message_type === 'image'
    const fileUrl = message.metadata?.presigned_url
    if (isFile && fileUrl) {
      if (message.message_type === 'image') {
        return (
          <img
            src={fileUrl}
            alt={message.metadata?.file_name || 'image'}
            onClick={() =>
              window.open(fileUrl, '_blank', 'noopener,noreferrer')
            }
            style={{
              maxWidth: '180px',
              maxHeight: '180px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'block'
            }}
          />
        )
      }
      const fileName =
        message.metadata?.file_name || message.message || 'Attachment'
      const ext = (fileName.split('.').pop() || '').toUpperCase().slice(0, 4)
      const badgeLabel = ext && ext.length <= 4 ? ext : 'FILE'
      return (
        <a
          href={fileUrl}
          target='_blank'
          rel='noopener noreferrer'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            color: 'inherit',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            padding: '8px 10px',
            maxWidth: '230px'
          }}
        >
          <span
            style={{
              flexShrink: 0,
              background: '#ef4444',
              color: '#ffffff',
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '0.5px',
              borderRadius: '5px',
              padding: '6px 5px'
            }}
          >
            {badgeLabel}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'underline'
            }}
          >
            {fileName}
          </span>
          <svg
            width='15'
            height='15'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{ flexShrink: 0, opacity: 0.6 }}
          >
            <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
            <polyline points='15 3 21 3 21 9' />
            <line x1='10' y1='14' x2='21' y2='3' />
          </svg>
        </a>
      )
    }
    // File/image still uploading (no presigned URL yet) — show the file name.
    if (isFile) {
      return message.metadata?.file_name || message.message || 'Attachment'
    }
    return message.message
  }

  return (
    <div className='chat-panel'>
      <div className='chat-tabs'>
        <button className={`chat-tab ${activeTab === 'chat' ? 'active' : ''}`}>
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{
              display: 'inline-block',
              marginRight: '6px',
              verticalAlign: 'middle'
            }}
          >
            <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
          </svg>
          Chat
        </button>
      </div>

      {activeTab === 'chat' ? (
        <div className='chat-content'>
          <div className='chat-messages'>
            {loading ? (
              <div
                className='message-time'
                style={{ textAlign: 'center', marginTop: '12px' }}
              >
                Loading messages…
              </div>
            ) : messages.length === 0 ? (
              <div
                className='message-time'
                style={{ textAlign: 'center', marginTop: '12px' }}
              >
                No messages yet
              </div>
            ) : (
              messages.map((message) => {
                const avatar = avatarForSender(message.sender_id)
                const senderName = nameForSender(message.sender_id)
                return (
                  <div
                    key={message.id}
                    className={`message ${
                      message.sender_id === selfUserId ? 'sent' : 'received'
                    }`}
                  >
                    <div className='message-meta'>
                      <span className='message-sender' title={senderName}>
                        {senderName}
                      </span>
                      <time
                        className='message-meta-time'
                        dateTime={parseMessageDate(
                          message.created_at
                        )?.toISOString()}
                        title={formatFullTimestamp(message.created_at)}
                      >
                        {formatTime(message.created_at)}
                      </time>
                    </div>
                    <div className='message-row'>
                      <img
                        className='message-avatar'
                        src={avatar || defaultAvatar}
                        alt={senderName}
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).src = defaultAvatar
                        }}
                      />
                      <div className='message-bubble'>
                        {renderMessageBody(message)}
                      </div>
                    </div>
                    {message.failed && (
                      <div
                        className='message-time'
                        role='status'
                        style={{ color: '#dc2626' }}
                      >
                        Not sent — please try again
                      </div>
                    )}
                  </div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {!loading && chatStatus !== 'ready' && (
            <div
              className='message-time'
              role='status'
              style={{
                textAlign: 'center',
                padding: '6px 12px',
                color: chatStatus === 'unavailable' ? '#dc2626' : '#6b7280'
              }}
            >
              {chatStatus === 'unavailable'
                ? 'Chat is unavailable right now. Please rejoin the call to retry.'
                : 'Reconnecting chat…'}
            </div>
          )}

          <div className='chat-input-container'>
            <div className='chat-input-wrapper'>
              <input
                type='file'
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <button
                className='attach-button'
                aria-label='Attach file'
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || chatStatus !== 'ready'}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
                </svg>
              </button>
              <input
                type='text'
                className='chat-input'
                placeholder={uploading ? 'Uploading file…' : 'Type a message'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className='send-button'
                onClick={handleSend}
                disabled={!inputValue.trim() || chatStatus !== 'ready'}
                aria-label='Send message'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                >
                  <path d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z' />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ChatPanel
